import { useState } from "react";
import { useNavigate } from "react-router-dom";
import benepayData from "../benepay-data.json";

//Try it out component
import TryItOut from "./TryItOut";

//Icons
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faLink, faDotCircle, faCopy } from "@fortawesome/free-solid-svg-icons";

//Utils
import { copyToClipboard } from "../utils/commonUtility";

/*
    CommonRequestPage

    Props:
    - title: string
    - description: string
    - path: string (endpoint path e.g., "/v1/requestRefund")
    - method: string ("get" | "post" | ...)
    - requestSchema: OpenAPI-style schema object { type, properties, required }
    - requiredFields: array of required field names
    - parameters: array of parameter objects (OpenAPI parameters)
    - payloadEncryption: boolean
    - responseDecryption: boolean
    - requestExample: object
    - responseExample: object
    - paymentMethods: array of [key, { description }] pairs OR []
    - showRedirectStatus: boolean (include 302 status in list)
    - uatBaseUrl: optional override base URL; defaults to benepay-data.json
*/

export default function CommonRequestPage({
    title = "",
    description = "",
    path = "",
    method = "post",
    requestSchema = null,
    requiredFields = [],
    parameters = [],
    payloadEncryption = false,
    responseDecryption = false,
    requestExample = {},
    responseExample = {},
    paymentMethods = [],
    showRedirectStatus = false,
    uatBaseUrl = benepayData.uatinfo.baseUrl,
}) {
    const navigate = useNavigate();

    const [requestCopied, setRequestCopied] = useState(false);
    const [responseCopied, setResponseCopied] = useState(false);

    const statusCodes = [
        { code: 200, message: "OK - The request was successful.", bgColor: "bg-green-100", txtColor: "text-green-600" },
        { code: 302, message: "Found - Redirecting to another URL.", bgColor: "bg-blue-100", txtColor: "text-blue-600" },
        { code: 400, message: "Bad Request - The request cannot be processed.", bgColor: "bg-yellow-100", txtColor: "text-yellow-600" },
        { code: 401, message: "Unauthorized - Authentication is required.", bgColor: "bg-orange-100", txtColor: "text-orange-600" },
        { code: 403, message: "Forbidden - Access is denied.", bgColor: "bg-red-100", txtColor: "text-red-600" },
        { code: 500, message: "Internal Server Error - Something went wrong.", bgColor: "bg-gray-100", txtColor: "text-gray-600" },
    ];

    const filteredStatusCodes = showRedirectStatus ? statusCodes : statusCodes.filter(s => s.code !== 302);

    const formFields = requestSchema?.properties ? Object.entries(requestSchema.properties) : [];

    const handleRequestCopy = (text) => {
        copyToClipboard(text, setRequestCopied);
    };

    const handleResponseCopy = (text) => {
        copyToClipboard(text, setResponseCopied);
    };

    return (
        <div className="grid grid-cols-12 gap-4">
            <div className="col-span-12 md:col-span-8">
                <div>
                    <p className="font-bold text-[32px]">{title}</p>
                </div>

                <div>
                    <hr className="border-t border-gray-300 my-4 w-full" />
                </div>

                {description && (
                    <div>
                        <p className="text-gray-700">{description}</p>
                    </div>
                )}

                <div className="mt-8">
                    <div>
                        <p className="text-gray-800 font-bold text-xl">Environment</p>
                    </div>

                    <table className="w-full border border-gray-200 text-left mt-3">
                        <tbody>
                            <tr className="border">
                                <td className="p-3 font-semibold text-gray-600 border">Test Environment</td>
                                <td className="p-3 border">
                                    <a href={uatBaseUrl + path} target="_blank" rel="noopener noreferrer" className="text-blue-600 underline">
                                        {uatBaseUrl + path}
                                    </a>
                                </td>
                            </tr>
                        </tbody>
                    </table>
                </div>

                {formFields.length > 0 && (
                    <div className="p-4 mt-6 bg-gray-50 rounded">
                        <h3 className="font-semibold">Request Parameters</h3>
                        <table className="w-full border border-gray-200 mt-3">
                            <thead>
                                <tr className="bg-gray-100 border border-gray-200">
                                    <th className="p-3 font-semibold border border-gray-200">Field</th>
                                    <th className="p-3 font-semibold border border-gray-200 w-32">Data Type</th>
                                    <th className="p-3 font-semibold border border-gray-200">Description</th>
                                </tr>
                            </thead>
                            <tbody>
                                {formFields.map(([fieldName, fieldDetails]) => (
                                    <tr key={fieldName} className="border">
                                        <td className="p-3 border">
                                            <p className="text-sm text-gray-600"><span className="font-bold">{fieldName}</span></p>
                                            <p className="text-sm">
                                                <span className="text-red-500">{requiredFields.includes(fieldName) ? "Required" : "Optional"}</span>
                                            </p>
                                        </td>
                                        <td className="p-3 border">
                                            <span className="pl-3">{fieldDetails.type}</span>
                                        </td>
                                        <td className="p-3 border">
                                            <p className="text-sm text-gray-600">{fieldDetails.description}</p>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                )}

                {paymentMethods?.length > 0 && (
                    <div className="p-4 mt-6 bg-gray-50 rounded">
                        <h3 className="font-semibold">Available options for payVia attribute</h3>
                        <table className="w-full border border-gray-200 mt-3">
                            <thead>
                                <tr className="bg-gray-100 border border-gray-200">
                                    <th className="p-3 font-semibold border border-gray-200 w-32 text-start">Payment Method</th>
                                    <th className="p-3 font-semibold border border-gray-200 w-32 text-start">Description</th>
                                </tr>
                            </thead>
                            <tbody>
                                {paymentMethods.map(([fieldName, fieldDetails]) => (
                                    <tr key={fieldName} className="border">
                                        <td className="p-3 border">
                                            <p className="text-sm text-gray-600"><span className="font-bold">{fieldName}</span></p>
                                        </td>
                                        <td className="p-3 border">
                                            <p className="text-sm text-gray-600">{fieldDetails.description}</p>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                        <p className="text-sm text-gray-600 mt-4">
                            <span className="font-bold">Note : </span>
                            <span>UPIQR/UPI_INTENT cannot be combined with other payment options. One or more of the other payment options can be combined and passed as comma separated values.</span>
                        </p>
                    </div>
                )}

                {parameters?.length > 0 && (
                    <div className="p-4 mt-7 bg-gray-50 rounded">
                        <h3 className="font-semibold text-gray-800">Parameters</h3>
                        <div className="mt-4 overflow-x-auto">
                            <table className="min-w-full border border-gray-200 text-left">
                                <thead>
                                    <tr className="bg-gray-100 border border-gray-200">
                                        <th className="p-3 font-semibold border border-gray-200 w-32">Field</th>
                                        <th className="p-3 font-semibold border border-gray-200 w-32">Data Type</th>
                                        <th className="p-3 font-semibold border border-gray-200">Description</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {parameters.map((param) => (
                                        <tr key={param.name} className="bg-white border border-gray-200">
                                            <td className="p-3 border border-gray-200">
                                                <p>
                                                    <span className="font-bold text-gray-600">{param.name}</span>
                                                </p>
                                                <p>
                                                    <span className="text-red-600">{param.required ? "Required" : "Optional"}</span>
                                                </p>
                                            </td>
                                            <td className="p-3 border border-gray-200">
                                                <span className="pl-4">{param.schema?.type || ""}</span>
                                            </td>
                                            <td className="p-3 border border-gray-200">
                                                <p>{param.description || ""}</p>
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    </div>
                )}

                {payloadEncryption && (
                    <div className="mt-4">
                        <div>
                            <p className="text-gray-800 font-bold text-xl">Request Encryption
                                <span onClick={() => navigate("/encryption")} className="pl-2 text-blue-400 cursor-pointer">
                                    <FontAwesomeIcon icon={faLink} />
                                </span>
                            </p>
                        </div>
                    </div>
                )}

                {requestExample && Object.keys(requestExample).length > 0 && (
                    <div className="p-4 mt-7 bg-gray-50 border-l-4 border-blue-500 rounded">
                        <div className="flex justify-between items-center">
                            <h3 className="font-semibold text-gray-800">Sample Request</h3>
                            <button
                                onClick={() => handleRequestCopy(JSON.stringify(requestExample, null, 2))}
                                className="text-gray-700 px-2 py-1 rounded text-sm"
                            >
                                {!requestCopied && <p className="font-bold text-lg"><FontAwesomeIcon className="" icon={faCopy} /></p>}
                                {requestCopied && <span className="text-md ml-1 text-green-600 font-bold">Copied!</span>}
                            </button>
                        </div>

                        <div className="mt-4 overflow-x-auto">
                            <pre className="bg-gray-900 text-white p-3 rounded mt-3">
                                <code>{JSON.stringify(requestExample, null, 2)}</code>
                            </pre>
                        </div>
                    </div>
                )}

                {responseDecryption && (
                    <div className="mt-7">
                        <div>
                            <p className="text-gray-800 font-bold text-xl">Response Decryption
                                <span onClick={() => navigate("/encryption")} className="pl-2 text-blue-400 cursor-pointer">
                                    <FontAwesomeIcon icon={faLink} />
                                </span>
                            </p>
                        </div>
                    </div>
                )}

                {responseExample && Object.keys(responseExample).length > 0 && (
                    <div className="p-4 mt-6 bg-gray-50 border-l-4 border-green-500 rounded">
                        <div className="flex justify-between items-center">
                            <h3 className="font-semibold">Sample Response</h3>
                            <button
                                onClick={() => handleResponseCopy(JSON.stringify(responseExample, null, 2))}
                                className="text-gray-700 px-2 py-1 rounded text-sm"
                            >
                                {!responseCopied && <p className="font-bold text-lg"><FontAwesomeIcon className="" icon={faCopy} /></p>}
                                {responseCopied && <span className="text-md ml-1 text-green-600 font-bold">Copied!</span>}
                            </button>
                        </div>

                        <pre className="bg-gray-900 text-white p-3 rounded mt-3">
                            <code>{JSON.stringify(responseExample, null, 2)}</code>
                        </pre>
                    </div>
                )}

                <div className="container mx-auto mt-8">
                    <h1 className="text-2xl font-bold mb-4">Status Codes</h1>
                    <div className="flex flex-col gap-4">
                        {filteredStatusCodes.map((status) => (
                            <div key={status.code} className={`p-4 border border-gray-300 shadow-md rounded-lg`}>
                                <p className="">
                                    <span className={`${status.txtColor}`}> <FontAwesomeIcon icon={faDotCircle} color={status.bgColor} /> </span>
                                    <span className={`font-semibold px-3 ${status.txtColor}`}>{status.code}</span>
                                    -
                                    <span className="pl-3 text-gray-700">{status.message}</span>
                                </p>
                            </div>
                        ))}
                    </div>
                </div>
            </div>

            <div className="col-span-12 md:col-span-4">
                <div className="md:sticky top-4 p-4">
                    <TryItOut
                        endpoint={path}
                        method={method}
                        requestSchema={requestSchema}
                        requiredFields={requiredFields}
                        payloadEncryption={payloadEncryption}
                        responseDecryption={responseDecryption}
                    />
                </div>
            </div>
        </div>
    );
}
